// Alternate panoramas tab on the setup screen: other renders taken from the same camera as a main one
// (a dark kitchen, a timber one). Pick a render, add its alternates and name each one; the same name at
// other spots switches together in the tour. Files upload straight away; the rest saves with the setup.
import { draftUrl, uploadFile, deleteFile, type PanoRow, type Project } from '../data';
import { html, raw, icon, toast, confirmDialog, $, $$ } from '../ui';
import { renderSizes } from '../process';

export interface AltEdit {
  id: string;
  /** The main render it belongs to. */
  pano: string;
  /** Scheme name, e.g. Dark. */
  set: string;
  base_key: string; mobile_key: string; full_key: string; bytes: number;
}

export interface AltState {
  alts: AltEdit[];
  /** Button name for the main renders ('' = Default). */
  schemeDefault: string;
  compare: boolean;
  /** Button order of the schemes, as the studio arranged it (names not in it go last, in natural order). */
  order: string[];
}

/** Trial projects can try a few alternates before publishing. */
export const TRIAL_ALTS = 3;

export function altFromRow(r: PanoRow, panos: PanoRow[]): AltEdit | null {
  const parent = panos.find((p) => p.name === r.name);
  if (!parent) return null;
  return { id: r.id, pano: parent.id, set: r.set_name, base_key: r.base_key, mobile_key: r.mobile_key ?? r.base_key, full_key: r.full_key ?? r.base_key, bytes: r.bytes };
}

/** Scheme names in button order: as arranged, then any others in natural order (Dark before Dark 2). */
export const schemeOrder = (alts: AltEdit[], order: string[] = []) => {
  const names = [...new Set(alts.map((a) => a.set.trim()).filter(Boolean))];
  const at = (n: string) => { const i = order.indexOf(n); return i < 0 ? 1e6 : i; };
  return names.sort((a, b) => (at(a) - at(b)) || a.localeCompare(b, undefined, { numeric: true }));
};

/** Problems that stop a save, in plain words (empty when fine). */
export function altProblems(alts: AltEdit[], panos: PanoRow[], label: (p: PanoRow) => string): string[] {
  const out: string[] = [];
  for (const p of panos) {
    const mine = alts.filter((a) => a.pano === p.id);
    const seen = new Set<string>();
    for (const a of mine) {
      const n = a.set.trim().toLowerCase();
      if (!n) out.push(`An alternate at ${label(p)} has no name.`);
      else if (n === 'default') out.push(`“Default” is the name for your main renders. Rename the alternate at ${label(p)}.`);
      else if (seen.has(n)) out.push(`Two alternates at ${label(p)} are both called ${a.set.trim()}.`);
      seen.add(n);
    }
  }
  return out;
}

export interface AltsPanel {
  /** Sends the alternates to the preview (after it loads, or a change). */
  push(): void;
}

export function altsPanel(host: HTMLElement, project: Project, panos: PanoRow[], st: AltState, label: (p: PanoRow) => string,
  send: (msg: object) => void, touch: () => void, pushSettings: () => void): AltsPanel {
  let open: string | null = null;
  const busy = new Map<string, { pano: string; name: string; f: number }>(); // uploads in progress
  const thumb = (id: string) => draftUrl(project.id, `thumbs/${id}.jpg`);
  const mine = (pano: string) => st.alts.filter((a) => a.pano === pano);

  const push = () => {
    const alts: Record<string, Array<{ id: string; set: string; base: string; mobile: string; full: string }>> = {};
    for (const a of st.alts) {
      if (!a.set.trim()) continue;
      (alts[a.pano] ??= []).push({ id: a.id, set: a.set.trim(), base: draftUrl(project.id, a.base_key), mobile: draftUrl(project.id, a.mobile_key), full: draftUrl(project.id, a.full_key) });
    }
    send({ avr: 'alts', alts });
  };
  const changed = () => { touch(); pushSettings(); push(); };

  const draw = () => {
    const names = schemeOrder(st.alts, st.order);
    const withAlts = panos.filter((p) => mine(p.id).length);
    host.innerHTML = html`
      <p class="muted">Other renders from the same camera, like a dark or timber kitchen. Buyers standing there get a button for each. Give alternates in different rooms the same name and they switch together.</p>
      <label class="field"><span>Name for your main renders</span><input id="alt-default" value="${st.schemeDefault}" placeholder="Default" maxlength="24"></label>
      <label class="toggle"><input type="checkbox" id="alt-compare" ${st.compare ? raw('checked') : ''}><span>Before and after slider <em>buyers can compare two side by side</em></span></label>
      ${names.length ? html`<div class="field"><span>Schemes <em class="muted">rename one to rename it everywhere</em></span>
        <div class="alt-names">${names.map((n, i) => { const c = st.alts.filter((a) => a.set.trim() === n).length; return html`<div class="alt-name"><input data-rename="${n}" value="${n}" maxlength="24" aria-label="Rename ${n}"><em>${c} spot${c === 1 ? '' : 's'}</em>
          <button class="btn small icon-only" data-move="${i}|-1" ${i === 0 ? raw('disabled') : ''} title="Move up" aria-label="Move ${n} up">${icon('up', 14)}</button>
          <button class="btn small icon-only" data-move="${i}|1" ${i === names.length - 1 ? raw('disabled') : ''} title="Move down" aria-label="Move ${n} down">${icon('down', 14)}</button></div>`; })}</div>
        <em class="muted tiny">The buttons show in this order everywhere in the tour, after ${st.schemeDefault.trim() || 'Default'}.</em></div>` : ''}
      <datalist id="alt-list">${names.map((n) => html`<option value="${n}">`)}</datalist>
      <div class="field"><span>Renders <em class="muted">${withAlts.length ? `${withAlts.length} with alternates` : 'pick one to add alternates'}</em></span></div>
      <div class="alt-spots">${panos.map((p) => {
        const list = mine(p.id), on = open === p.id;
        const up = [...busy.values()].filter((b) => b.pano === p.id);
        return html`<div class="alt-spot ${on ? 'open' : ''} ${list.length ? 'has' : ''}">
          <button class="alt-head" data-open="${p.id}"><img src="${thumb(p.id)}" alt="" loading="lazy">
            <span class="alt-t"><strong>${label(p)}</strong><em>${list.length ? `${list.length} alternate${list.length === 1 ? '' : 's'}` : p.name}</em></span>${icon(on ? 'up' : 'down', 15)}</button>
          ${on ? html`<div class="alt-body">
            <div class="alt-row alt-main"><button class="alt-th" data-view="${p.id}|default" title="Show in the preview"><img src="${thumb(p.id)}" alt=""></button>
              <div class="alt-f"><b>${st.schemeDefault.trim() || 'Default'}</b><em>Main render · ${p.name}</em></div>
              <button class="btn small icon-only" data-view="${p.id}|default" title="Show in the preview" aria-label="Show the main render in the preview">${icon('eye', 14)}</button></div>
            ${list.map((a) => html`<div class="alt-row"><button class="alt-th" data-view="${p.id}|${a.id}" title="Show in the preview"><img src="${thumb(a.id)}" alt=""></button>
              <div class="alt-f"><input list="alt-list" data-name="${a.id}" value="${a.set}" placeholder="Name, e.g. Dark" maxlength="24" aria-label="Name for this alternate"></div>
              <button class="btn small icon-only" data-view="${p.id}|${a.id}" title="Show in the preview" aria-label="Show ${a.set} in the preview">${icon('eye', 14)}</button>
              <button class="btn small icon-only" data-del="${a.id}" title="Remove this alternate" aria-label="Remove ${a.set}">${icon('trash', 14)}</button></div>`)}
            ${up.map((b) => html`<div class="alt-row busy">${icon('spin', 16)}<div class="alt-f"><em>${b.name} · ${b.f < 0.25 ? 'reading' : `uploading ${Math.round(b.f * 100)}%`}</em></div></div>`)}
            <label class="btn small alt-add">${icon('plus', 14)}Add alternate panorama<input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden data-add="${p.id}"></label>
            <p class="muted tiny">Render it from the same camera as ${p.name}, at the same size. It uses the same position and heading, so hotspots stay put.</p>
          </div>` : ''}
        </div>`;
      })}</div>`.html;
    wire();
  };

  const view = (pano: string, which: string) => {
    const a = st.alts.find((x) => x.id === which);
    send({ avr: 'alt-show', pano, set: a ? a.set.trim() : 'default' });
  };

  const wire = () => {
    $<HTMLInputElement>('#alt-default', host).oninput = (e) => { st.schemeDefault = (e.target as HTMLInputElement).value; touch(); pushSettings(); };
    $<HTMLInputElement>('#alt-compare', host).onchange = (e) => { st.compare = (e.target as HTMLInputElement).checked; touch(); pushSettings(); };
    $$<HTMLButtonElement>('[data-open]', host).forEach((b) => (b.onclick = () => {
      const id = b.dataset.open!;
      open = open === id ? null : id;
      draw();
      if (open) view(open, 'default');
    }));
    $$<HTMLButtonElement>('[data-view]', host).forEach((b) => (b.onclick = () => { const [p, w] = b.dataset.view!.split('|'); view(p, w); }));
    $$<HTMLInputElement>('[data-name]', host).forEach((inp) => {
      inp.oninput = () => { const a = st.alts.find((x) => x.id === inp.dataset.name)!; a.set = inp.value; changed(); };
      inp.onchange = () => draw(); // the scheme list and counts
      inp.onfocus = () => { const a = st.alts.find((x) => x.id === inp.dataset.name)!; view(a.pano, a.id); };
    });
    $$<HTMLButtonElement>('[data-move]', host).forEach((b) => (b.onclick = () => {
      const [i, d] = b.dataset.move!.split('|').map(Number);
      const list = schemeOrder(st.alts, st.order);
      [list[i], list[i + d]] = [list[i + d], list[i]];
      st.order = list;
      changed(); draw();
    }));
    $$<HTMLInputElement>('[data-rename]', host).forEach((inp) => {
      inp.onchange = () => {
        const from = inp.dataset.rename!, to = inp.value.trim();
        if (!to || to === from) { inp.value = from; return; }
        st.order = schemeOrder(st.alts, st.order).map((n) => (n === from ? to : n));
        for (const a of st.alts) if (a.set.trim() === from) a.set = to;
        changed(); draw();
      };
    });
    $$<HTMLButtonElement>('[data-del]', host).forEach((b) => (b.onclick = async () => {
      const a = st.alts.find((x) => x.id === b.dataset.del)!;
      if (!(await confirmDialog({ title: `Remove ${a.set.trim() || 'this alternate'}?`, body: 'It comes out of the tour when you save.', confirm: 'Remove', danger: true }))) return;
      st.alts = st.alts.filter((x) => x !== a);
      changed(); draw();
    }));
    $$<HTMLInputElement>('[data-add]', host).forEach((inp) => (inp.onchange = () => {
      const files = [...(inp.files ?? [])];
      inp.value = '';
      void add(inp.dataset.add!, files);
    }));
  };

  /** Reads each file, uploads its sizes and adds it with the next unused scheme name. */
  const add = async (pano: string, files: File[]) => {
    for (const f of files) {
      if (project.state === 'trial' && st.alts.length + busy.size >= TRIAL_ALTS) {
        toast(`Trial projects can try ${TRIAL_ALTS} alternate panoramas. Publish to add more.`, 'info', 6000);
        break;
      }
      const id = crypto.randomUUID();
      busy.set(id, { pano, name: f.name, f: 0 });
      draw();
      try {
        const s = await renderSizes(f);
        if (Math.abs(s.width / s.height - 2) > 0.05) toast(`${f.name} isn’t 2:1. Alternates need the same size as the main render.`, 'error', 7000);
        const bytes = s.thumb.size + s.base.size + s.mobile.size + s.full.size;
        if (project.storage_bytes + bytes > project.storage_limit) throw new Error('This project is out of space');
        const v = Date.now().toString(36);
        const k = { base: `panos/${id}-2k-${v}.jpg`, mobile: `panos/${id}-1k-${v}.jpg`, full: `panos/${id}-full-${v}.jpg` };
        const jobs: Array<[string, Blob]> = [[`thumbs/${id}.jpg`, s.thumb], [k.base, s.base], [k.mobile, s.mobile], [k.full, s.full]];
        const sent = new Map<string, number>();
        const prog = () => {
          const b = busy.get(id); if (!b) return;
          b.f = 0.25 + 0.75 * ([...sent.values()].reduce((x, y) => x + y, 0) / bytes);
          const el = host.querySelector('.alt-row.busy em');
          if (el) el.textContent = `${b.name} · uploading ${Math.round(b.f * 100)}%`;
        };
        await Promise.all(jobs.map(([key, blob]) => uploadFile(project.id, key, blob, (x) => { sent.set(key, x * blob.size); prog(); })));
        // the next scheme name this render doesn't have yet
        const used = new Set(mine(pano).map((a) => a.set.trim()));
        const free = schemeOrder(st.alts, st.order).find((n) => !used.has(n));
        let n = st.alts.length + 1;
        while (st.alts.some((a) => a.set.trim() === `Scheme ${n}`)) n++;
        st.alts.push({ id, pano, set: free ?? `Scheme ${n}`, base_key: k.base, mobile_key: k.mobile, full_key: k.full, bytes });
        project.storage_bytes += bytes;
        changed();
        busy.delete(id);
        open = pano;
        draw();
        view(pano, id);
      } catch (e) {
        busy.delete(id);
        draw();
        toast(`Couldn’t add ${f.name}: ${e instanceof Error ? e.message : 'unknown error'}`, 'error', 7000);
        void deleteFile(project.id, `thumbs/${id}.jpg`);
      }
    }
  };

  draw();
  return { push };
}

